import EventBus from 'eventing-bus';
import Menu from '@mui/material/Menu';
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import DataTable from 'react-data-table-component';
import { useDispatch, useSelector } from 'react-redux';
import CircularProgress from '@mui/material/CircularProgress';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import './index.css';
import LeadsFilters from './LeadsFilters';
import LeadSocialIcons from './components/LeadSocialIcons';
import LeadDetailsDrawer from './components/LeadDetailsDrawer';
import TitleRoute from '../../components/titleRoute';
import { setAssistant } from '../../store/Assistant/index';
import { countQualifyingFilters, MIN_QUALIFYING_FILTERS, MULTI_FILTER_KEYS } from './filterUtils';
import { searchLeads, revealLeads, getLeadsCredits, setLeadsFilters, getLeadsFilterOptions, clearLeadsSearch } from '../../store/Leads/index';

const LeadsPage = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const aiFilterTimer = useRef(null);

  const { records, revealedData, revealingIds, inCrmMap, loading, revealLoading, pagination, filters, filterOptions, optionsLoading, creditsBalance, error, hasSearched } = useSelector(s => s['Leads']);
  const { currentBranding } = useSelector(s => s['Settings'] || {});

  // Reason: branding reports modules.leads === false while the leads service is down — fail open when the flag is absent
  const isLeadsAvailable = currentBranding?.['modules']?.['leads'] !== false;

  const [ui, setUi] = useState({ selectedRows: [], clearRows: false, actionAnchor: null, actionRow: null });
  const [detail, setDetail] = useState({ open: false, lead: null });

  // On mount: read filters from URL and run search only when enough of them are present
  useEffect(() => {
    if (!isLeadsAvailable) return;
    dispatch(getLeadsCredits());
    dispatch(getLeadsFilterOptions());
    const params = new URLSearchParams(location['search']);
    const urlFilters = {};
    ['q', 'jobTitle', 'city'].forEach(k => { if (params.get(k)) urlFilters[k] = params.get(k); });
    MULTI_FILTER_KEYS.forEach(k => { const vals = params.getAll(k); if (vals.length) urlFilters[k] = vals; });
    ['hasEmail', 'hasPhone', 'hasLinkedin'].forEach(k => { if (params.get(k) === 'true') urlFilters[k] = true; });
    if (Object.keys(urlFilters).length) {
      dispatch(setLeadsFilters(urlFilters));
      if (countQualifyingFilters(urlFilters) >= MIN_QUALIFYING_FILTERS) dispatch(searchLeads({ ...urlFilters, page: 1 }));
    }
  }, []);

  useEffect(() => {
    const unsub = EventBus.on('leads:setFilters', (aiFilters) => {
      clearTimeout(aiFilterTimer.current);
      aiFilterTimer.current = setTimeout(() => {
        dispatch(setLeadsFilters(aiFilters));
        // Reason: Milo's tool already blocks <2 filters — this guard covers stale clients/other publishers
        if (countQualifyingFilters(aiFilters) >= MIN_QUALIFYING_FILTERS) dispatch(searchLeads({ ...aiFilters, page: 1 }));
        else EventBus.publish('error', `Milo needs at least ${MIN_QUALIFYING_FILTERS} filters — ask it to narrow the search.`);
      }, 100);
    });
    return () => { unsub(); clearTimeout(aiFilterTimer.current); };
  }, []);

  const pushFiltersToUrl = (activeFilters) => {
    const params = new URLSearchParams();
    Object.entries(activeFilters).forEach(([k, v]) => {
      if (Array.isArray(v)) v.forEach(x => { if (x !== '' && x != null) params.append(k, x); });
      else if (v !== '' && v !== false && v != null) params.set(k, v);
    });
    history.replace({ search: params.toString() });
  };

  const handleSearch = useCallback((page = 1) => {
    // Reason: pagination re-enters here — never let a below-minimum filter set reach the API
    if (countQualifyingFilters(filters) < MIN_QUALIFYING_FILTERS) return;
    pushFiltersToUrl(filters);
    dispatch(searchLeads({ ...filters, page, limit: pagination['limit'] }));
  }, [filters, pagination]);

  const handleFilterChange = (patch) => dispatch(setLeadsFilters(patch));

  // Only update state — search fires on Apply
  const handleSearchInput = (e) => dispatch(setLeadsFilters({ q: e['target']['value'] }));

  // Reason: Clear returns to the pre-search empty state — a filterless search is no longer allowed
  const handleClearFilters = () => {
    dispatch(clearLeadsSearch());
    history.replace({ search: '' });
  };

  const openActionMenu = (e, row) => setUi(prev => ({ ...prev, actionAnchor: e['currentTarget'], actionRow: row }));
  const closeActionMenu = () => setUi(prev => ({ ...prev, actionAnchor: null, actionRow: null }));

  const handleRevealRow = (row, fields) => {
    const prospectId = row['id'] || row['_id'];
    dispatch(revealLeads({ prospectIds: [prospectId], fields }));
  };

  const handleRowClick = (row) => {
    const id = row['id'] || row['_id'];
    if (revealedData[id] || revealingIds[id]) return;
    // Reason: CRM sync happens server-side inside /reveal — no onSuccess save needed here
    dispatch(revealLeads({ prospectIds: [id], fields: ['email', 'phone', 'linkedin'] }));
  };

  const handleBulkReveal = () => {
    const ids = ui['selectedRows'].map(r => r['id'] || r['_id']);
    dispatch(revealLeads({ prospectIds: ids, fields: ['email', 'phone', 'linkedin'] }));
  };

  const handleAskMilo = () => {
    dispatch(setAssistant(true));
    setTimeout(() => EventBus.publish('assistant:prefill', { message: '@setLeadsFilters Find me CEOs of SaaS companies in the US' }), 150);
  };

  const handleOpenDetail = (row) => setDetail({ open: true, lead: row });
  const getColumns = () => COLUMNS(revealedData, revealingIds, inCrmMap, handleRevealRow, openActionMenu, handleOpenDetail);

  // Reason: keep formatting in sync with the sidebar "Free Credits" widget — both show raw creditsBalance (1 credit ≈ $0.01)
  const creditsDisplay = Math.round(parseFloat(creditsBalance) || 0).toLocaleString('en-US');

  if (!isLeadsAvailable) return (
    <div className="leads-page">
      <div className="leads-header">
        <div className="leads-header-left">
          <TitleRoute />
          <p className="records-title">Search prospects</p>
        </div>
      </div>
      <div className="leads-body">
        <div className="leads-table-card leads-unavailable-card">
          <div className="leads-empty-state">
            <i className="fa fa-plug leads-empty-icon" />
            <div className="leads-empty-title">Leads is temporarily unavailable</div>
            <div className="leads-empty-sub">We're working on it — please check back soon.</div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="leads-page">
      <div className="leads-header">
        <div className="leads-header-left">
          <TitleRoute />
          <p className="records-title">
            {pagination['total'] > 0 ? `${pagination['total'].toLocaleString()} records` : 'Search prospects'}
          </p>
        </div>
        <div className="leads-header-right">
          <div className="leads-credits-widget">
            <i className="leads-credits-icon">
              <svg width="19" height="15" viewBox="0 0 19 15" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <g clipPath="url(#clip0_credits_leads)">
                  <path d="M-0.000488281 3.50156C0.0742854 3.01996 0.366493 2.64019 0.595242 2.2309C0.828418 1.81374 1.0916 1.41233 1.30707 0.986807C1.6101 0.387141 2.11826 0.126909 2.74646 0.0427887C3.67671 -0.082654 4.5794 0.0836191 5.46832 0.334996C7.85223 1.00993 9.98328 2.17925 11.9481 3.66981C11.9672 3.68456 11.9913 3.69293 12.0204 3.70818C11.9008 3.86117 11.8 3.99055 11.707 4.1091C11.1615 3.7372 10.6356 3.35448 10.0866 3.00914C8.51338 2.01937 6.85459 1.2131 5.04083 0.761502C4.31966 0.581946 3.59013 0.449125 2.84042 0.552431C2.7199 0.569156 2.59839 0.592769 2.4818 0.627696C1.8964 0.803808 1.66864 1.20572 1.82015 1.80046C1.96429 2.36569 2.2806 2.83992 2.63873 3.28659C3.53847 4.40918 4.64729 5.29613 5.8358 6.0906C6.13538 6.29082 6.44235 6.48021 6.75817 6.68289C6.67011 6.83145 6.58698 6.97165 6.48367 7.1458C5.89827 6.74783 5.31632 6.37347 4.75748 5.96664C3.76673 5.24546 2.85567 4.43574 2.10843 3.45581C1.7808 3.02636 1.50778 2.56542 1.3292 2.00757C1.21016 2.21073 1.10931 2.38193 1.00945 2.55312C0.914014 2.71742 0.829893 2.88911 0.722652 3.04554C0.522928 3.33726 0.488492 3.65603 0.595242 3.97628C0.699531 4.29013 0.850063 4.58824 0.980425 4.89373C1.00109 4.88931 1.02224 4.88537 1.0429 4.88094C1.16588 4.67187 1.28887 4.4628 1.41923 4.24192C1.56779 4.32752 1.70898 4.40918 1.8659 4.4997C1.71439 4.76435 1.56582 5.01573 1.42759 5.27301C1.4025 5.32024 1.39119 5.41371 1.41923 5.4447C1.81622 5.88547 2.22108 6.31886 2.64365 6.77587C2.81238 6.48612 2.95307 6.24507 3.10459 5.98435C3.25512 6.07044 3.39532 6.15111 3.55175 6.24114C3.38892 6.52744 3.23249 6.80194 3.05638 7.11136C3.66195 7.55361 4.26358 7.9934 4.88194 8.44499C4.80274 8.56994 4.71616 8.70719 4.63007 8.84346C4.60203 8.83854 4.58137 8.841 4.56759 8.83214C3.23987 7.96831 2.00069 7.00019 0.999118 5.75904C0.577532 5.23563 0.212026 4.67777 0.0610033 4.00776C0.0398502 3.91282 0.0201729 3.81739 -0.000488281 3.72244C-0.000488281 3.64865 -0.000488281 3.57535 -0.000488281 3.50156Z" />
                  <path d="M7.0254 15C6.67564 14.9296 6.28849 14.874 5.91462 14.7692C5.55403 14.6684 5.28937 14.4244 5.10195 14.0928C4.74333 13.4582 4.38865 12.8202 4.00592 12.2004C3.70338 11.7104 3.68125 11.2126 3.85834 10.6862C4.08316 10.0167 4.48556 9.45441 4.94305 8.93001C6.16846 7.52702 7.65754 6.45559 9.26074 5.53371C10.6726 4.72153 12.1504 4.05988 13.7393 3.67175C14.3901 3.51285 15.0498 3.38938 15.7213 3.43955C16.0642 3.46513 16.4085 3.54827 16.7391 3.64863C17.0662 3.748 17.3004 3.98511 17.4711 4.28519C17.8194 4.89764 18.1519 5.52043 18.5317 6.11321C18.9125 6.70697 18.9006 7.2963 18.6399 7.91515C18.3571 8.58615 17.9237 9.15581 17.4332 9.68316C16.0597 11.1599 14.4196 12.2806 12.6492 13.2202C11.3411 13.9138 9.98142 14.4805 8.5253 14.782C8.04715 14.8804 7.55767 14.9242 7.0254 15ZM17.538 5.44123C17.4549 5.64144 17.3968 5.80231 17.323 5.95579C16.9413 6.74682 16.3805 7.40453 15.7513 8.00272C13.7634 9.89321 11.4528 11.2623 8.87949 12.192C7.83807 12.5683 6.77058 12.8389 5.65045 12.8123C5.41531 12.8069 5.18115 12.7671 4.92534 12.741C5.1295 13.0952 5.36169 13.4179 5.51222 13.7755C5.7636 14.3727 6.77747 14.5075 7.26055 14.4013C7.14051 14.1878 7.02147 13.9767 6.89701 13.7559C7.05393 13.6668 7.19512 13.5866 7.34614 13.501C7.47208 13.7195 7.60441 13.9133 7.69886 14.1243C7.79331 14.3349 7.9158 14.3934 8.13963 14.3339C8.64534 14.1996 9.15645 14.0864 9.69463 13.9581C9.53081 13.6683 9.3916 13.4209 9.24746 13.166C9.40488 13.0775 9.54656 12.9973 9.6961 12.9132C9.80433 13.0986 9.90321 13.2674 10.0011 13.4366C10.188 13.7598 10.1905 13.7662 10.528 13.6265C10.9732 13.442 11.4134 13.2457 11.866 13.0504C11.7056 12.7695 11.561 12.5162 11.411 12.2535C11.5635 12.1654 11.7047 12.0838 11.8581 11.9947C12.0185 12.2697 12.171 12.531 12.3353 12.8128C12.8656 12.5059 13.3777 12.2102 13.8997 11.9082C13.7354 11.6209 13.5848 11.3582 13.4299 11.0871C13.5883 10.9961 13.729 10.9154 13.878 10.8299C14.0345 11.0975 14.182 11.3498 14.3414 11.6228C14.8343 11.2549 15.3145 10.8963 15.798 10.5352C15.6273 10.2366 15.4694 9.9611 15.3036 9.67135C15.4547 9.58379 15.5954 9.50262 15.7498 9.41309C15.9008 9.66939 16.0445 9.91387 16.2108 10.1958C16.6604 9.72301 17.0889 9.27338 17.508 8.8331C17.322 8.50892 17.1607 8.22802 16.9905 7.9304C17.1425 7.84235 17.2837 7.7602 17.4357 7.67165C17.5685 7.89548 17.6949 8.10799 17.8514 8.37167C17.9989 8.03912 18.1396 7.7602 18.2469 7.46946C18.3369 7.22497 18.3738 6.95244 18.2533 6.71435C18.0383 6.28981 17.7884 5.88249 17.538 5.44123ZM5.81574 12.3386C6.49412 12.2835 7.14937 12.1664 7.79478 11.9874C9.64789 11.4733 11.3431 10.6208 12.9438 9.571C14.1176 8.80112 15.2087 7.9304 16.1139 6.84717C16.5079 6.37541 16.8645 5.87659 17.0298 5.27102C17.204 4.632 16.9747 4.20746 16.3273 4.05349C15.9982 3.97527 15.6436 3.92903 15.31 3.96297C14.7217 4.022 14.1299 4.11203 13.5588 4.26256C11.8267 4.71907 10.2387 5.50764 8.73093 6.46051C7.40862 7.2963 6.17633 8.24426 5.17672 9.46228C4.78268 9.9429 4.42849 10.4501 4.28878 11.0724C4.17023 11.6002 4.37094 11.9903 4.88501 12.1532C5.18508 12.2486 5.50484 12.2791 5.81574 12.3386Z" />
                  <path d="M7.87017 5.87021C7.78654 6.01828 7.70882 6.15602 7.62716 6.30065C7.59912 6.29327 7.57452 6.29229 7.55583 6.28147C6.18973 5.49979 4.87529 4.64432 3.73892 3.54534C3.44967 3.26592 3.19977 2.94321 2.95183 2.62493C2.84902 2.4931 2.78064 2.32633 2.72849 2.16547C2.57501 1.69321 2.80327 1.31836 3.2893 1.22194C3.78566 1.12356 4.26677 1.22637 4.74148 1.35083C6.09528 1.70551 7.35364 2.29288 8.56871 2.97273C9.23627 3.3466 9.87824 3.76622 10.531 4.16665C10.5822 4.19813 10.6289 4.237 10.6894 4.28127C10.592 4.42295 10.5 4.55675 10.4007 4.70187C10.2806 4.62267 10.1739 4.55232 10.0671 4.48247C8.55051 3.48926 6.96895 2.62149 5.24276 2.04691C4.78674 1.8954 4.30661 1.81423 3.83337 1.71929C3.69367 1.69125 3.53969 1.71338 3.39605 1.73454C3.20468 1.76307 3.14811 1.85358 3.23174 2.03215C3.32422 2.22991 3.43393 2.4257 3.56921 2.59591C4.06114 3.21574 4.65589 3.73375 5.29884 4.18485C6.09085 4.74024 6.91435 5.25087 7.72456 5.78019C7.76933 5.80921 7.81557 5.83676 7.87017 5.87021Z" />
                  <path d="M12.9714 1.25491C13.1692 1.25491 13.3261 1.25491 13.5013 1.25491C13.5013 1.46398 13.5013 1.66322 13.5013 1.88803C13.7226 1.88803 13.9268 1.88803 14.1398 1.88803C14.1398 2.07054 14.1398 2.22746 14.1398 2.41145C13.9337 2.41145 13.7305 2.41145 13.5096 2.41145C13.5096 2.63724 13.5096 2.83648 13.5096 3.04948C13.3301 3.04948 13.1682 3.04948 12.9803 3.04948C12.9803 2.84779 12.9803 2.64364 12.9803 2.4203C12.756 2.4203 12.5567 2.4203 12.3452 2.4203C12.3452 2.23878 12.3452 2.07742 12.3452 1.89688C12.5459 1.89688 12.7456 1.89688 12.971 1.89688C12.9714 1.67601 12.9714 1.47186 12.9714 1.25491Z" />
                  <path d="M2.01641 9.03632C2.01641 9.24932 2.01641 9.44954 2.01641 9.66845C2.23286 9.66845 2.43259 9.66845 2.64609 9.66845C2.64609 9.84899 2.64609 10.0108 2.64609 10.1909C2.44194 10.1909 2.2427 10.1909 2.01838 10.1909C2.01838 10.4127 2.01838 10.6169 2.01838 10.8324C1.83145 10.8324 1.6696 10.8324 1.48906 10.8324C1.48906 10.6213 1.48906 10.4216 1.48906 10.2032C1.27212 10.2032 1.07289 10.2032 0.857422 10.2032C0.857422 10.0236 0.857422 9.86276 0.857422 9.67534C1.05911 9.67534 1.26327 9.67534 1.48414 9.67534C1.48414 9.44905 1.48414 9.24883 1.48414 9.03632C1.66321 9.03632 1.82554 9.03632 2.01641 9.03632Z" />
                  <path d="M6.0517 11.6411C5.95086 11.6175 5.7467 11.5944 5.56174 11.522C5.25231 11.4015 5.10916 11.0985 5.17901 10.7728C5.27986 10.3035 5.57551 9.94786 5.88789 9.60941C6.58643 8.85183 7.39714 8.22609 8.2467 7.65053C9.81941 6.5855 11.4703 5.66509 13.2713 5.04477C13.8173 4.85685 14.3919 4.74321 14.9606 4.634C15.18 4.5917 15.4279 4.62613 15.6493 4.68024C16.0852 4.78699 16.2637 5.13873 16.1383 5.57212C16.035 5.92877 15.818 6.21802 15.5809 6.49252C14.9837 7.18467 14.2773 7.75531 13.5369 8.28266C11.8078 9.51495 9.96896 10.5436 7.95843 11.2466C7.37402 11.4517 6.7773 11.6116 6.0517 11.6411ZM15.1874 5.09593C14.5557 5.16283 13.9694 5.33845 13.3933 5.54654C11.5731 6.20474 9.89566 7.13597 8.31213 8.24085C7.51127 8.79969 6.74041 9.39787 6.11024 10.153C5.95135 10.3429 5.82295 10.5647 5.71424 10.7881C5.62962 10.9612 5.69702 11.0586 5.88395 11.0867C6.01038 11.1054 6.14074 11.1128 6.26815 11.1049C6.84814 11.0675 7.40058 10.9037 7.94023 10.703C10.2749 9.8352 12.403 8.60586 14.3157 7.00954C14.7682 6.63174 15.2007 6.22786 15.5052 5.71527C15.5844 5.58245 15.6724 5.37141 15.6183 5.26614C15.5657 5.16234 15.3379 5.14856 15.1874 5.09593Z" />
                </g>
                <defs>
                  <clipPath id="clip0_credits_leads">
                    <rect width="18.8277" height="15" fill="white" />
                  </clipPath>
                </defs>
              </svg>
            </i>
            <span className="leads-credits-label">Credits:</span>
            {creditsDisplay}
          </div>
          <button className="btn-style-new leads-action-btn" onClick={handleBulkReveal} disabled={!ui['selectedRows'].length || revealLoading}>
            {revealLoading ? <i className="fa fa-spinner fa-spin icon" /> : <i className="fa fa-unlock-alt icon" />}
            Reveal ({ui['selectedRows'].length})
          </button>
        </div>
      </div>

      <div className="leads-body">
        <LeadsFilters filters={filters} filterOptions={filterOptions} optionsLoading={optionsLoading} onChange={handleFilterChange} onApply={() => handleSearch(1)} onClear={handleClearFilters} onSearchChange={handleSearchInput} />

        <div className="leads-table-card">
          <div className="leads-table-wrapper">
            <DataTable
              columns={getColumns()}
              data={records}
              selectableRows
              onSelectedRowsChange={({ selectedRows }) => setUi(prev => ({ ...prev, selectedRows }))}
              clearSelectedRows={ui['clearRows']}
              pagination
              paginationServer
              paginationTotalRows={pagination['total']}
              paginationPerPage={pagination['limit']}
              onChangePage={(p) => handleSearch(p)}
              onRowClicked={handleRowClick}
              progressPending={loading}
              progressComponent={<CircularProgress size={32} sx={{ my: 4, color: 'var(--primary-color)' }} />}
              className="leads-table"
              fixedHeader
              fixedHeaderScrollHeight="100%"
              noDataComponent={
                error ? (
                  <div className="leads-empty-state">
                    <i className="fa fa-exclamation-circle leads-empty-icon leads-empty-icon--error" />
                    <div className="leads-empty-title">Search failed</div>
                    <div className="leads-empty-sub">{error}</div>
                    <button className="btn-style-border leads-empty-milo-btn" onClick={() => handleSearch(1)}>
                      <i className="fa fa-refresh icon" /> Try Again
                    </button>
                  </div>
                ) : hasSearched ? (
                  <div className="leads-empty-state">
                    <i className="fa fa-search-minus leads-empty-icon" />
                    <div className="leads-empty-title">No prospects match your filters</div>
                    <div className="leads-empty-sub">Try broadening your filters — remove a country, drop a Contact Info checkbox, or clear the keyword.</div>
                    <button className="btn-style-border leads-empty-milo-btn" onClick={handleClearFilters}>
                      <i className="fa fa-eraser icon" /> Clear Filters
                    </button>
                  </div>
                ) : (
                  <div className="leads-empty-state">
                    <i className="fa fa-users leads-empty-icon" />
                    <div className="leads-empty-title">Find your next prospects</div>
                    <div className="leads-empty-sub">Pick at least {MIN_QUALIFYING_FILTERS} filters on the left and click Apply, or let Milo set them for you.</div>
                    <button className="btn-style-new leads-empty-milo-btn" onClick={handleAskMilo}>
                      <i className="fa fa-magic icon" /> Ask Milo to find prospects
                    </button>
                  </div>
                )
              }
            />
          </div>
        </div>
      </div>

      {/* Row action menu */}
      <Menu anchorEl={ui['actionAnchor']} open={Boolean(ui['actionAnchor'])} onClose={closeActionMenu}>
        <MenuItem className="leads-actions-menu-item" onClick={() => { handleRevealRow(ui['actionRow'], ['email', 'phone', 'linkedin']); closeActionMenu(); }}>Reveal All Fields</MenuItem>
        <MenuItem className="leads-actions-menu-item" onClick={() => { handleOpenDetail(ui['actionRow']); closeActionMenu(); }}>View Details</MenuItem>
      </Menu>

      <LeadDetailsDrawer
        open={detail['open']}
        lead={detail['lead']}
        onClose={() => setDetail({ open: false, lead: null })}
      />
    </div>
  );
};

export default LeadsPage;

// Reason: search records store ISO-2 country codes (e.g. "US"); display the full name so the Location column matches the country filter labels
const COUNTRY_NAMES = { US: 'United States', GB: 'United Kingdom', UK: 'United Kingdom', CA: 'Canada', AU: 'Australia', DE: 'Germany', FR: 'France', IN: 'India', SG: 'Singapore', NL: 'Netherlands', SE: 'Sweden' };

const formatCountry = (code) => COUNTRY_NAMES[code] || code || '';

const formatLocation = (row) => [row['city'], row['state'], formatCountry(row['country'])].filter(Boolean).join(', ') || '—';

// Reason: employeeCount is a raw headcount integer; show a compact human-readable count
const formatEmployeeCount = (count) => {
  const n = Number(count);
  if (!n || Number.isNaN(n)) return '—';
  if (n >= 1000000) return `${(n / 1000000).toFixed(1).replace(/\.0$/, '')}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1).replace(/\.0$/, '')}k`;
  return String(n);
};

const COLUMNS = (revealedData, revealingIds, inCrmMap, onReveal, onMenuOpen, onOpenDetail) => [
  {
    name: 'Name',
    minWidth: '130px',
    grow: 2,
    cell: row => {
      const id = row['id'] || row['_id'];
      const revealed = revealedData[id];
      const leadWithRevealed = revealed ? { ...row, linkedinUrl: revealed['linkedinUrl'] } : row;
      return (
        <div className="leads-name-cell">
          <div className="leads-name-cell-top">
            <span className="leads-name-text">{`${row['firstName'] || ''} ${row['lastName'] || ''}`.trim() || '—'}</span>
            {inCrmMap[id] && (
              <Tooltip title="Added to Contacts" placement="top" arrow>
                <i className="fa fa-check-circle leads-in-crm-icon" aria-label="Added to Contacts" />
              </Tooltip>
            )}
          </div>
          {revealed && <LeadSocialIcons lead={leadWithRevealed} type="person" className="leads-name-social-row" />}
        </div>
      );
    },
  },
  { name: 'Job Title', selector: row => row['jobTitle'] || row['title'] || '—', minWidth: '100px', grow: 1.5 },
  {
    name: 'Company',
    minWidth: '130px',
    grow: 1.5,
    cell: row => {
      const id = row['id'] || row['_id'];
      const revealed = revealedData[id];
      return (
        <div className="leads-company-cell">
          <span className="leads-company-name">{row['companyName'] || row['company'] || '—'}</span>
          {revealed && <LeadSocialIcons lead={row} type="company" className="leads-company-social-row" />}
        </div>
      );
    },
  },
  { name: 'Location', selector: row => formatLocation(row), minWidth: '100px', grow: 1.5 },
  { name: 'Industry', selector: row => row['industry'] || '—', minWidth: '90px', grow: 1 },
  { name: 'Company Size', selector: row => formatEmployeeCount(row['employeeCount']), minWidth: '90px', grow: 1 },
  { name: 'Revenue', selector: row => row['revenue'] || '—', minWidth: '110px', grow: 1 },
  {
    name: 'Email',
    minWidth: '140px',
    grow: 2,
    cell: row => {
      const id = row['id'] || row['_id'];
      const revealed = revealedData[id];
      const loading = revealingIds[id];
      if (revealed) return revealed['email']
        ? <span className="leads-revealed-value">{revealed['email']}</span>
        : <span className="leads-lock-cell"><i className="fa fa-ban leads-lock-icon" /> N/A</span>;
      if (!row['hasEmail']) return <span className="leads-lock-cell"><i className="fa fa-ban leads-lock-icon" /> N/A</span>;
      if (loading) return <span className="leads-lock-cell"><i className="fa fa-spinner fa-spin leads-lock-icon" /></span>;
      return (
        <div className="leads-lock-cell">
          <i className="fa fa-lock leads-lock-icon" />
          <button className="leads-reveal-btn" onClick={() => onReveal(row, ['email', 'phone', 'linkedin'])}>1 Credit</button>
        </div>
      );
    },
  },
  {
    name: 'Phone',
    minWidth: '130px',
    grow: 1.5,
    cell: row => {
      const id = row['id'] || row['_id'];
      const revealed = revealedData[id];
      const loading = revealingIds[id];
      if (revealed) return revealed['phone']
        ? <span className="leads-revealed-value">{revealed['phone']}</span>
        : <span className="leads-lock-cell"><i className="fa fa-ban leads-lock-icon" /> N/A</span>;
      if (!row['hasPhone']) return <span className="leads-lock-cell"><i className="fa fa-ban leads-lock-icon" /> N/A</span>;
      if (loading) return <span className="leads-lock-cell"><i className="fa fa-spinner fa-spin leads-lock-icon" /></span>;
      return (
        <div className="leads-lock-cell">
          <i className="fa fa-lock leads-lock-icon" />
          <button className="leads-reveal-btn" onClick={() => onReveal(row, ['email', 'phone', 'linkedin'])}>1 Credit</button>
        </div>
      );
    },
  },
  {
    name: '',
    width: '72px',
    cell: row => (
      <div className="leads-row-actions">
        <Tooltip title="View Details">
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); onOpenDetail(row); }}>
            <i className="fa fa-info-circle leads-detail-icon" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Actions">
          <IconButton size="small" onClick={(e) => onMenuOpen(e, row)}>
            <i className="fa fa-ellipsis-v leads-menu-icon" />
          </IconButton>
        </Tooltip>
      </div>
    ),
    right: true,
  },
];
